import React from "react";
import { CodePane, Box, FlexBox, Markdown } from "spectacle";

import { okaidiaTheme } from "./presentationTheme";

type CodePaneProps = React.ComponentProps<typeof CodePane>;

type DefaultCodePaneProps = Omit<
  CodePaneProps,
  "children" | "highlightRanges"
> & {
  highlightRanges?: number | number[];
  source: string;
};

const defaultTheme = {
  ...okaidiaTheme,
  size: {
    width: "100%",
  },
};

const DefaultCodePane = ({
  language = "jsx",
  highlightRanges = [],
  source,
  ...props
}: DefaultCodePaneProps) => {
  return (
    <CodePane
      language={language}
      highlightRanges={highlightRanges}
      {...props}
      theme={defaultTheme}
    >
      {source}
    </CodePane>
  );
};

export const TitleSlide: React.FC<any> = ({ children, ...rest }) => (
  <FlexBox
    flexDirection="column"
    justifyContent="center"
    alignItems="center"
    maxWidth="100%"
    height="100%"
    {...rest}
  >
    {children}
  </FlexBox>
);

export const TitleDummy = () => null;

interface LeftContentRightContentProps {
  leftContent: React.ReactNode;
  leftStyle?: React.CSSProperties;
  rightContent: React.ReactNode;
  rightStyle?: React.CSSProperties;
}

export const LeftContentRightContent = ({
  leftContent,
  leftStyle = {},
  rightContent,
  rightStyle,
}: LeftContentRightContentProps) => (
  <FlexBox alignItems="flex-start" flexWrap="wrap">
    <Box minWidth={800} style={{ marginRight: 5, flex: "1", ...leftStyle }}>
      {leftContent}
    </Box>
    <Box minWidth={800} style={{ marginLeft: 5, flex: "1", ...rightStyle }}>
      {rightContent}
    </Box>
  </FlexBox>
);

interface LeftCodeRightContentProps extends LeftContentRightContentProps {
  leftSource: string;
  leftLanguage?: string;
}

export const LeftCodeRightContent = ({
  leftSource,
  leftLanguage = "jsx",
  ...otherProps
}: LeftCodeRightContentProps) => (
  <LeftContentRightContent
    {...otherProps}
    leftContent={
      <DefaultCodePane language={leftLanguage} source={leftSource} />
    }
  />
);

interface LeftCodeRightCodeProps extends LeftCodeRightContentProps {
  rightSource: string;
  rightLanguage?: string;
}

export const LeftCodeRightCode = ({
  leftSource,
  leftLanguage = "jsx",
  rightSource,
  rightLanguage = "jsx",
  ...otherProps
}: LeftCodeRightCodeProps) => (
  <LeftContentRightContent
    {...otherProps}
    leftContent={
      <DefaultCodePane source={leftSource} language={leftLanguage} />
    }
    rightContent={
      <DefaultCodePane source={rightSource} language={rightLanguage} />
    }
  />
);

interface LeftContentRightMarkdownProps extends LeftContentRightContentProps {
  children: string;
}

export const LeftContentRightMarkdown = ({
  children,
  ...otherProps
}: LeftContentRightMarkdownProps) => {
  return (
    <LeftContentRightContent
      {...otherProps}
      rightContent={<Markdown>{children}</Markdown>}
    />
  );
};

interface LeftCodeRightMarkdownProps extends LeftCodeRightContentProps {
  children: string;
}

export const LeftCodeRightMarkdown = ({
  children,
  ...otherProps
}: LeftCodeRightMarkdownProps) => {
  return (
    <LeftCodeRightContent
      {...otherProps}
      rightContent={<Markdown>{children}</Markdown>}
    />
  );
};
